import { collection, orderBy, onSnapshot, addDoc, doc, setDoc, getDoc, query, where, getDocs, limit, updateDoc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import moment from 'moment';
import { COLLECTIONS } from "../config";
import { firebase } from "../firebase-config";
import { getMetadata, getStorage, listAll, ref, list } from "firebase/storage";

const auth = getAuth();

const msg_collection = COLLECTIONS.CHAT_CHANNELS;
const chat_collection = COLLECTIONS.CHAT_MESSAGES;

/**
 * Get Tasks list from standardtasks collection (shown while create client)
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getChannelList(companyId, onSuccess, onError) {

  if (!companyId) {
    console.warn("getChannelList: No companyId provided, skipping query.");
    return;
  }

  const q = query(collection(firebase, msg_collection), where("companyId", "==", companyId), orderBy("createdOn", "desc"));

  return onSnapshot(q, (querySnapshot) => {
    let results = [];
    querySnapshot?.forEach((doc) => {
      let data = doc?.data();
      results?.push({ id: doc.id, ...data });
    });
    onSuccess && onSuccess(results);
  }, (err) => {
    onError && onError(err);
    console.error(`Encountered error: ${err}`);
  });
}

/**
 * Send channel and save it to the database
 * @param {*} formData : data to be saved and sent as channels
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function saveChannel(formData, onSuccess, onError) {
  const currentUserId = auth.currentUser.uid;

  try {
    const docRef = await addDoc(collection(firebase, msg_collection), {
      ...formData,
      createdBy: currentUserId,
      createdOn: new Date().getTime(),
    });
    const docdata = { ...formData, id: docRef.id };
    onSuccess && onSuccess({ status: true, data: docdata });
  } catch (error) {
    console.error(`ERROR: ${msg_collection} ${error}`);
    onError && onError({ status: false, error });
  }
}

/**
 * Update channel in the database
 * @param {*} id
 * @param {*} formData : data to be saved and sent as update channel
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function updateChannel(id, formData, onSuccess, onError) {
  const currentUserId = auth.currentUser.uid;
  const channelRef = doc(firebase, msg_collection, id);

  try {
    await setDoc(channelRef, {
      ...formData,
      updatedAt: new Date().getTime(),
      updatedBy: currentUserId,
    }, { merge: true });

    const docdata = { ...formData, id };
    onSuccess && onSuccess({ status: true, data: docdata });
  } catch (error) {
    console.error(`ERROR: ${msg_collection} ${error}`);
    onError && onError({ status: false, error });
  }
}

/**
 * Permanently Remove Channel from the database
 * @param {*} id
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function deleteChannel(id, onSuccess, onError) {
  const channelRef = doc(firebase, msg_collection, id);
  const currentUserId = auth.currentUser.uid;
  try {
    await updateDoc(channelRef, {
      deleted: true,
      status: false,
      deletedBy: currentUserId,
      deletedAt: serverTimestamp(),
    });
    onSuccess && onSuccess({ status: true, message: "Delete successfully" });
  } catch (error) {
    onError && onError({ status: false, error });
  }
}

/**
 * Get details of a particular Channel
 * @param {*} id : id of Channel
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getChannel(id, onSuccess, onError) {
  try {
    const channelRef = doc(firebase, msg_collection, id);
    const channelSnap = await getDoc(channelRef);
    
    if (!channelSnap.exists()) {
      onError && onError({ status: false, error: "Channel not found" });
    } else {
      const data = channelSnap.data();
      onSuccess && onSuccess({ status: true, data: { id: channelSnap.id, ...data } });
    }
  } catch (error) {
    onError && onError({ status: false, error });
  }
}

/**
 * Send message to channel and save it to the database
 * @param {*} formData : Message Data {channelId, sender:{id, firstName, lastName}, type:{text/image}, readBy:[], message, image}
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function sendMessageToChannel(formData, onSuccess, onError) {
  try {
    const docRef = await addDoc(collection(firebase, chat_collection), {
      ...formData,
      createdOn: new Date().getTime(),
      updatedOn: new Date().getTime(),
    });
    const docdata = { ...formData, id: docRef.id };
    onSuccess && onSuccess({ status: true, data: docdata });
  } catch (error) {
    console.error(`ERROR: ${chat_collection} ${error}`);
    onError && onError({ status: false, error });
  }
}

/**
 * Get particular Channel Message
 * @param {*} channelId : id of Channel
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getChannelMessage(channelId, onSuccess, onError) {
  try {
    const q = query(collection(firebase, chat_collection), where("channelId", "==", channelId), orderBy("updatedOn"));
    const querySnapshot = await getDocs(q);
    let results = [];

    querySnapshot.forEach((doc) => {
      let data = doc.data();
      results.push({ id: doc.id, ...data });
    });

    onSuccess && onSuccess(results);
  } catch (error) {
    onError && onError({ status: false, error });
  }
}

export async function getRecentClientMessagesByCompany(companyId, clientIds, onSuccess, onError) {
  try {
    const limitedClientIds = clientIds?.slice(0, 10);

    const channelsRef = collection(firebase, msg_collection);
    const channelsQuery = query(
      channelsRef,
      where("companyId", "==", companyId),
      where("client", "in", limitedClientIds),
      where("status", "==", true)
    );

    const channelSnapshot = await getDocs(channelsQuery);
    const channels = [];

    const messagePromises = channelSnapshot.docs?.map(async (doc) => {
      const channelData = doc.data();
      const channelId = doc.id;
      const channelName = channelData.groupName;
      channels.push({ id: channelId, ...channelData });

      const messagesRef = collection(firebase, chat_collection);
      const messagesQuery = query(
        messagesRef,
        where("channelId", "==", channelId),
        where("type", "==", "text"),
        orderBy("updatedOn", "desc"),
        limit(5)
      );

      const messagesSnapshot = await getDocs(messagesQuery);
      const messages = messagesSnapshot.docs?.map(msgDoc => ({
        id: msgDoc.id,
        ...msgDoc.data()
      }));

      return {
        channelId,
        channelName,
        client: channelData.client,
        messages,
      };
    });

    const result = await Promise.all(messagePromises);
    onSuccess?.({ result, channels });
  } catch (error) {
    console.error("Error getting recent client messages:", error);
    onError && onError({ status: false, error });
  }
}

/**
 * Get messages and mark them as read by the current user
 * @param {*} channelId
 * @param {*} onSuccess
 * @param {*} onError
 */
export function getMessages(channelId, onSuccess, onError) {
  getChannel(channelId, (channel) => {
    const channelData = channel.data;
    const currentUserId = auth.currentUser.uid;

    if (channelData.readMsgUsers) {
      if (!channelData.readMsgUsers.includes(currentUserId)) {
        channelData.readMsgUsers.push(currentUserId);
        updateChannel(channelId, channelData);
      }
    } else {
      channelData.readMsgUsers = [currentUserId];
      updateChannel(channelId, channelData);
    }
  });

  const q = query(collection(firebase, chat_collection), where("channelId", "==", channelId), orderBy("updatedOn"));

  return onSnapshot(q, (querySnapshot) => {
    let results = [];
    let imagesObject = [];

    querySnapshot.forEach((doc) => {
      let data = doc.data();
      
      let isNeedtoAdd = true;

      if (data.type === 'image') {
        if (imagesObject.length > 0) {
          if (data.sender.userId === imagesObject[0].sender.userId) {
            let duration = moment.duration(moment(data.createdOn).diff(moment(imagesObject[0].createdOn)));
            let diffMins = duration.asMinutes();

            if (diffMins <= 1 && data.message === "") {
              imagesObject.push({ id: doc.id, ...data });
              isNeedtoAdd = false;
            } else if (diffMins <= 1 && data.message !== "") {
              imagesObject.push({ id: doc.id, ...data });
              isNeedtoAdd = true;
            } else {
              isNeedtoAdd = true;
            }
          }
        } else {
          imagesObject.push({ id: doc.id, ...data });
          isNeedtoAdd = false;
        }
      }

      if (isNeedtoAdd) {
        if (imagesObject.length > 0) {
          if (doc.id === imagesObject[imagesObject.length - 1].id) {
            results.push({ ...imagesObject[imagesObject.length - 1], imagesObj: imagesObject });
            imagesObject = [];
          } else {
            results.push({ ...imagesObject[imagesObject.length - 1], imagesObj: imagesObject });
            if (data.type === 'image') {
              imagesObject = [];
              imagesObject.push({ id: doc.id, ...data });
            } else {
              results.push({ id: doc.id, ...data });
              imagesObject = [];
            }
          }
        } else {
          results.push({ id: doc.id, ...data });
          imagesObject = [];
        }
      } else {
        const lastDoc = querySnapshot.docs[querySnapshot.size - 1];
        if (lastDoc.id === doc.id) {
          if (imagesObject.length > 0) {
            results.push({ ...imagesObject[imagesObject.length - 1], imagesObj: imagesObject });
            imagesObject = [];
          }
        }
      }
    });
    onSuccess && onSuccess(results);
  }, (err) => {
    onError && onError(err);
    console.error(`Encountered error: ${err}`);
  });
}

// export async function moveCompanyFiles (oldCompanyId, newCompanyId) {
//   const storage = getStorage();
//   const oldRef = ref(storage, `${oldCompanyId}/`);

//   try {
//     const result = await listAll(oldRef);

//     for (const itemRef of result.items) {
//       const blob = await getBlob(itemRef);
//       const newPath = itemRef.fullPath.replace(oldCompanyId, newCompanyId);
//       const newRef = ref(storage, newPath);

//       await uploadBytes(newRef, blob);
//       await deleteObject(itemRef);
//       console.log(`Moved: ${itemRef.fullPath} → ${newPath}`);
//     }

//     // Handle subfolders recursively
//     for (const folderRef of result.prefixes) {
//       await moveCompanyFiles(folderRef.fullPath, folderRef.fullPath.replace(oldCompanyId, newCompanyId));
//     }
//   } catch (error) {
//     console.error("Error moving files:", error);
//   }
// };

// export async function getUsedStorage(companyId) {
//   if(!companyId) {
//     return 0;
//   }
//   const storage = getStorage();
//   const companyRef = ref(storage, `${companyId}/`);

//   try {
//     const result = await listAll(companyRef);
//     const metadataPromises = [];

//     for (const item of result.items) {
//       metadataPromises.push(getMetadata(item));
//     }
  
//     const folderPromises = result.prefixes.map(async (folder) => {
//       const subfolderRef = ref(storage, folder.fullPath);
//       const subfolderResult = await listAll(subfolderRef);

//       return Promise.all(subfolderResult.items.map(item => getMetadata(item)));
//     });

//     const subfolderMetadata = await Promise.all(folderPromises);
//     const fileMetadata = await Promise.all(metadataPromises);

//     const totalSize = [...fileMetadata, ...subfolderMetadata.flat()].reduce((sum, meta) => sum + meta.size, 0);

//     return totalSize;
//   } catch (error) {
//     return 0;
//   }
// }

export async function getUsedStorage(companyId) {
  if (!companyId) {
    return 0;
  }

  const storage = getStorage();
  const companyRef = ref(storage, `${companyId}/`);

  let totalSize = 0;

  async function processBatch(items) {
    const batchSize = 250;
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const metas = await Promise.allSettled(batch.map(item => getMetadata(item)));
      for (const res of metas) {
        if (res.status === "fulfilled") {
          totalSize += res.value.size;
        }
      }
    }
  }

  async function listAllPages(folderRef) {
    let page = await list(folderRef, { maxResults: 100 });
    await processBatch(page.items);

    for (const sub of page.prefixes) {
      await listAllPages(sub);
    }

    while (page.nextPageToken) {
      page = await list(folderRef, { maxResults: 100, pageToken: page.nextPageToken });
      await processBatch(page.items);
      for (const sub of page.prefixes) {
        await listAllPages(sub);
      }
    }
  }

  try {
    await listAllPages(companyRef);
    return totalSize;
  } catch (error) {
    console.error("Error in getUsedStorage:", error);
    return 0;
  }
}

export async function getCompanyStorageData(companyId) {
  if (!companyId) {
    return {
      totalSize: 0,
      totalCount: 0,
      documents: { size: 0, count: 0 },
      images: { size: 0, count: 0 },
      others: { size: 0, count: 0 }
    };
  }

  const storage = getStorage();
  const companyRef = ref(storage, `${companyId}/`);

  const isDocument = (name) =>
    /\.(pdf|docx?|xlsx?|pptx?)$/i.test(name);

  const isImage = (name) =>
    /\.(jpe?g|png|webp|gif|bmp|svg)$/i.test(name);

  const stats = {
    documents: { size: 0, count: 0 },
    images: { size: 0, count: 0 },
    others: { size: 0, count: 0 },
    totalSize: 0,
    totalCount: 0
  };

  const processItems = async (items) => {
    const metas = await Promise.all(items.map(item => getMetadata(item)));
    for (const meta of metas) {
      stats.totalSize += meta.size;
      stats.totalCount += 1;

      if (isDocument(meta.name)) {
        stats.documents.size += meta.size;
        stats.documents.count += 1;
      } else if (isImage(meta.name)) {
        stats.images.size += meta.size;
        stats.images.count += 1;
      } else {
        stats.others.size += meta.size;
        stats.others.count += 1;
      }
    }
  };

  try {
    const result = await listAll(companyRef);

    await processItems(result.items);

    for (const folder of result.prefixes) {
      const subfolderRef = ref(storage, folder.fullPath);
      const subfolderResult = await listAll(subfolderRef);
      await processItems(subfolderResult.items);
    }

    return stats;
  } catch (error) {
    return {
      totalSize: 0,
      totalCount: 0,
      documents: { size: 0, count: 0 },
      images: { size: 0, count: 0 },
      others: { size: 0, count: 0 }
    };
  }
}


export async function fetchLatestChatMessages(channelIds) {
  try {
    if (!channelIds || channelIds.length === 0) return [];

    const messagesRef = collection(firebase, chat_collection);

    const chunks = [];
    while (channelIds.length) {
      chunks.push(channelIds.splice(0, 10));
    }

    let allMessages = [];

    for (const chunk of chunks) {
      const q = query(
        messagesRef,
        where("type", "==", "text"),
        where("channelId", "in", chunk),
        orderBy("createdOn", "desc")
      );

      const snapshot = await getDocs(q);

      const messages = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdOn: data.createdOn?.seconds
            ? data.createdOn.seconds * 1000
            : data.createdOn,
        };
      });

      allMessages = [...allMessages, ...messages];
    }

    // Sort combined results and return the latest 5
    allMessages.sort((a, b) => b.createdOn - a.createdOn);
    return allMessages.slice(0, 50);
  } catch (error) {
    console.error("Error fetching chat messages:", error);
    return [];
  }
}